import React, { useEffect, useState } from 'react';
import CommonInput from '../Components/CommonInput';
import PasswordEyeToggle from '../Components/PasswordEyeToggle';
import CommonButton from '../Components/CommonButton';
import { useLocation, useNavigate } from 'react-router-dom';
import { sign_in, getUserProfileService, alert_chat_initialize } from '../services';
import ButtonLoader from '../Components/ButtonLoader';
import ProfileCompletionModal from '../Components/ProfileCompletionModal';

function SignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({ email: '', password: '' });
  const [resMsg, setResMsg] = useState('');
  const [errResMsg, setErrResMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [showPass, setShowPass] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileData, setProfileData] = useState(null);

  const location = useLocation();
  useEffect(() => {
    sessionStorage.setItem("otp_source_page", location.pathname);
  }, [location.pathname]);

  const validate = () => {
    let emailError = '';
    let passwordError = '';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const passwordRegex =
      /^(?=.{8,}$)(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()]).*$/;

    if (!email) {
      emailError = 'Email is required';
      setIsLoading(false);
    } else if (!emailRegex.test(email)) {
      emailError = 'Enter a valid email.';
      setIsLoading(false);
    }

    if (!password) {
      passwordError = 'Password is required.';
      setIsLoading(false);
    } else if (!passwordRegex.test(password)) {
      passwordError =
        'Password must be at least 8 characters and include lowercase, uppercase, number & special character.';
      setIsLoading(false);
    }

    setErrors({ email: emailError, password: passwordError });
    return !emailError && !passwordError;
  };

  const onChangeHandler = (e) => {
    const { name, value } = e.target;

    if (name === 'email') {
      setEmail(value);
      setErrResMsg('');
    } else if (name === 'password') {
      setPassword(value);
      setErrResMsg('');
    }

    // Clear specific error when user types
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: '',
    }));
  };

  // Check if profile is incomplete
  const isProfileIncomplete = (profile) => {
    if (!profile) return true;
    const requiredFields = ['industry_group', 'region', 'primary_regulatory_profile', 'customer_size'];
    return requiredFields.some(field => !profile?.organization[field] || profile?.organization[field] === '');
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (!validate()) {
      return;
    }

    try {
      const passData = { emailId: email, password: password };
      const response = await sign_in(passData);
      setIsLoading(false);

      const successMessage = response?.statusCode?.response_data?.message;
      const errorMessage = response?.errors?.message;
      const errResMsg = response?.error?.message
      if (successMessage) {
        setResMsg(successMessage);
        setErrResMsg('');
        sessionStorage.setItem('access_token', response?.statusCode?.response_data?.data?.access_token);
        sessionStorage.setItem('organization_id', response?.statusCode?.response_data?.data?.organization_id);
        sessionStorage.setItem('refresh_token', response?.statusCode?.response_data?.data?.refresh_token);
        sessionStorage.setItem('organization_id', response?.statusCode?.response_data?.data?.organization_id);
        localStorage.setItem('organization_id', response?.statusCode?.response_data?.data?.organization_id);
        sessionStorage.setItem('is_otp_allowed', '/');
        localStorage.setItem('email', email);
        const loginRiskRole = response?.statusCode?.response_data?.data?.risk_role;
        if (loginRiskRole) {
          localStorage.setItem('risk_role', loginRiskRole);
        } else {
          localStorage.removeItem('risk_role');
        }

        // Save RBAC/PBAC flags — consumed by getUserFlags() throughout the app.
        // Flags: is_platform_admin, is_org_admin, is_org_user, role_slug, primary_org_plan
        const loginFlags = response?.statusCode?.response_data?.data?.flags;
        if (loginFlags) {
          sessionStorage.setItem('user_flags', JSON.stringify(loginFlags));
        }


        // Initialize alert chat session after login
        try {
          const orgId = response?.statusCode?.response_data?.data?.organization_id;
          await alert_chat_initialize({ organization_id: orgId });
        } catch (initError) {
          console.error('Error initializing alert chat:', initError);
        }

        // Fetch user profile after successful login
        try {
          const profileResponse = await getUserProfileService();
          const fetchedProfileData = profileResponse?.statusCode?.response_data?.data;
          if (fetchedProfileData) {
            localStorage.setItem('user_profile', JSON.stringify(fetchedProfileData));
            setProfileData(fetchedProfileData);

            // Check if profile is incomplete
            if (isProfileIncomplete(fetchedProfileData)) {
              setShowProfileModal(true);
              return; // Don't navigate yet, wait for profile completion
            }
          }
        } catch (profileError) {
          console.error('Error fetching user profile:', profileError);
        }

        // Navigate after setting user_id and tracking
        const redirectTo = sessionStorage.getItem('redirect_after_login') || '/dashboard';
        sessionStorage.removeItem('redirect_after_login');
        navigate(redirectTo);
      } else if (errResMsg) {
        setErrResMsg(errResMsg);
        setResMsg('');
        setIsLoading(false);
      }
      else if (errorMessage) {
        setErrResMsg(errorMessage)
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      setErrResMsg("Something went wrong. Please try again.");
    }
  };

  const handleProfileComplete = () => {
    setShowProfileModal(false);
    const redirectTo = sessionStorage.getItem('redirect_after_login') || '/dashboard';
    sessionStorage.removeItem('redirect_after_login');
    navigate(redirectTo);
  };


  return (
    <div className="sign-in-wrapper">
      {/* Left: Animated tagline */}
      <div className="signin-left-panel">
        <div className="signin-left-content">
           <p className="animated-tagline-novaai">NOVA AI</p>
          <p className="animated-tagline">Unified Cyber Risk Intelligence Platform</p>
        </div>
      </div>

      {/* Right: Login Form */}
      <div className="signin-form-panel">
        <div className="sign-in-wrper">
          <div className="logo_wrper">
            <img src="/assets/small_logo.svg" alt="Logo" width={300} height={90} />
          </div>

          <div className="card">
            <h1 className="title">Login</h1>
            <p className="auth_txt_color font_400 font_16">
              Enter your company email below to login to your account.
            </p>

            <form onSubmit={submitHandler} noValidate>
              <div className="form-group">
                <label htmlFor="email" className="font_500 font_16 light_black">
                  Work Email
                </label>
                <CommonInput
                  id="email"
                  type="email"
                  name="email"
                  value={email}
                  onChange={onChangeHandler}
                  placeholder="m@example.com"
                  maxLength={50}
                />
                {errors.email && <p className="error-message">{errors.email}</p>}
              </div>

              <div className="form-group">
                <label htmlFor="password" className="font_500 font_16 light_black">
                  Password
                </label>
                <CommonInput
                  id="password"
                  type={showPass ? 'text' : 'password'}
                  name="password"
                  value={password}
                  onChange={onChangeHandler}
                  placeholder="Type Password"
                  maxLength={30}
                />
                {errors.password && <p className="error-message">{errors.password}</p>}
                <span className='eye_icon cursor_pointer'>
                  <PasswordEyeToggle visible={showPass} onClick={() => setShowPass(!showPass)} />
                </span>
              </div>

              <p className="forgot_txt">
                <button type="button" onClick={() => navigate("/forget-password")}>
                  Forgot your password?
                </button>
              </p>

              {resMsg && <p className="success-message">{resMsg}</p>}
              {errResMsg && <p className="error-message">{errResMsg}</p>}

              <CommonButton
                type="submit"
                text={isLoading ? <ButtonLoader /> : "Login"}
                disable={isLoading}
              />
            </form>

            <p className="footer-text">
              Don't have an account?{' '}
              <button type="button" onClick={() => navigate("/sign-up")}>
                Sign up
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Profile Completion Modal */}
      <ProfileCompletionModal
        show={showProfileModal}
        onHide={() => { }}
        onSuccess={handleProfileComplete}
        existingProfile={profileData}
      />
    </div>
  );
}

export default SignIn;
